import React, { useEffect, useState } from "react";
import {
  showApiErrorToast,
  showSuccessToast,
} from "../../../utils/toastUtils";
import { Plus } from "lucide-react";

import NotificationForm from "./NotificationForm";
import {
  sendAdminNotification,
  listAdminNotifications,
} from "./pushNotificationsApi";

import FormButton from "../../../components/form/FormButton";
import useTableController from "../../../components/table/useTableController";
import TableToolbar from "../../../components/table/TableToolbar";
import Paginator from "../../../components/table/Paginator";

import "./pushNotifications.css";

const PushNotificationsPage = () => {
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);
  const [sending, setSending] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const tc = useTableController(logs, {
    searchKeys: ["title", "message", "target_value", "data.type"],
    initialPageSize: 10,
  });

  const loadLogs = async () => {
    setLoadingLogs(true);

    try {
      const { items } = await listAdminNotifications({
        limit: 50,
      });

      setLogs(Array.isArray(items) ? items : []);
    } catch (e) {
      showApiErrorToast(e, "Failed to load notification logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  useEffect(() => {
    loadLogs();
  }, []);

  const handleSend = async (payload) => {
    try {
      setSending(true);

      const res = await sendAdminNotification(payload);

      if (!res?.success) {
        showApiErrorToast(
          { response: { data: { message: res?.message } } },
          "Failed to send notification",
        );
      } else {
        showSuccessToast(res.message || "Notification sent successfully");
        setFormOpen(false);
        await loadLogs();
      }
    } catch (e) {
      showApiErrorToast(e, "Failed to send notification");
      throw e;
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      <div className="sa-head-row pn-head-row">
        <h1 className="sa-h1 sa-topbar-managed-page-title">Push Notifications</h1>

        <FormButton
          type="button"
          variant="primary"
          className="pn-new-btn"
          onClick={() => setFormOpen(true)}
        >
          <Plus className="lucide" />
          Compose Notification
        </FormButton>
      </div>

      <TableToolbar
        searchTerm={tc.searchTerm}
        onSearchChange={tc.setSearch}
        searchPlaceholder="Search notifications..."
        filterDefs={[
          {
            id: "target_type",
            label: "Target",
            options: [
              { value: "all", label: "All targets" },
              { value: "single", label: "Single user" },
              { value: "role", label: "Role / type" },
              { value: "country", label: "Country" },
            ],
          },
        ]}
        filters={tc.filters}
        onFilterChange={tc.setFilter}
      />

      <div className="sa-card">
        {loadingLogs ? (
          <div className="sa-placeholder pn-loading">Loading…</div>
        ) : tc.total === 0 ? (
          <div className="sa-empty">
            <div>No notifications have been sent yet.</div>

            <FormButton
              type="button"
              variant="primary"
              onClick={() => setFormOpen(true)}
            >
              Compose first notification
            </FormButton>
          </div>
        ) : (
          <div className="sa-table-wrap pn-table-wrap">
            <table className="sa-table pn-table">
              <thead>
                <tr>
                  <th style={{ width: 60 }}>S.N.</th>
                  <th>Title</th>
                  <th>Message</th>
                  <th>Type</th>
                  <th>Target</th>
                  <th>Status</th>
                  <th style={{ width: 160 }}>Sent At</th>
                </tr>
              </thead>

              <tbody>
                {tc.rows.map((row, idx) => {
                  const data = row?.data || {};
                  const type = data?.type || "-";

                  return (
                    <tr key={row._id || row.id || idx}>
                      <td>{(tc.page - 1) * tc.pageSize + idx + 1}</td>

                      <td>{row.title}</td>

                      <td className="sa-ellipsis" title={row.message}>
                        {row.message}
                      </td>

                      <td>{type}</td>

                      <td>
                        {row.target_type === "all"
                          ? "All users"
                          : row.target_type === "single"
                          ? `Single: ${row.target_value || "-"}`
                          : row.target_type === "role"
                          ? `Role: ${row.target_value || "-"}`
                          : row.target_type === "country"
                          ? `Country: ${row.target_value || "-"}`
                          : row.target_type || "-"}
                      </td>

                      <td>{row.status}</td>

                      <td>
                        {row.createdAt
                          ? new Date(row.createdAt).toLocaleString()
                          : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>

            <Paginator
              page={tc.page}
              totalPages={tc.totalPages}
              onPage={tc.setPage}
              pageSize={tc.pageSize}
              onPageSizeChange={tc.setPageSize}
              pageSizeOptions={[5, 10, 20, 50]}
            />
          </div>
        )}
      </div>

      {formOpen && (
        <NotificationForm
          open={formOpen}
          loading={sending}
          onCancel={() => {
            if (!sending) setFormOpen(false);
          }}
          onSubmit={handleSend}
        />
      )}
    </>
  );
};

export default PushNotificationsPage;